import React, { useEffect, useState } from "react";
import { GraduationCap, FileText, CheckCircle2, CreditCard, Link2 } from "lucide-react";
import api from "../lib/api";
import PortalShell from "./PortalShell";

const STATUS_COLORS = {
  Draft: "bg-slate-100 text-slate-600", Submitted: "bg-blue-100 text-blue-700",
  "Under Review": "bg-amber-100 text-amber-700", Offer: "bg-green-100 text-green-700",
  Rejected: "bg-red-100 text-red-700", Accepted: "bg-emerald-100 text-emerald-700",
  Declined: "bg-orange-100 text-orange-700", Withdrawn: "bg-slate-100 text-slate-500",
};
const REVIEW_COLORS = {
  Approved: "bg-green-100 text-green-700", Rejected: "bg-red-100 text-red-700",
  Reviewed: "bg-blue-100 text-blue-700", "Offer Received": "bg-teal-100 text-teal-700",
};

export default function ParentPortal() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/portal/parent/overview").then((r) => setData(r.data)).catch(() => {}); }, []);

  return (
    <PortalShell allow="parent">
      <h1 className="font-display text-3xl font-bold text-[#012a1e]" data-testid="parent-portal-heading">Parent Dashboard</h1>
      <p className="text-slate-500 mt-1">Follow your child's study-abroad journey with GDIH.</p>

      {data && !data.linked && (
        <div className="mt-6 bg-white rounded-2xl border border-dashed border-slate-200 p-10 text-center" data-testid="parent-not-linked">
          <Link2 className="mx-auto text-slate-300" size={36} />
          <p className="text-slate-500 mt-3">Your account isn't linked to a student yet.</p>
          <p className="text-slate-400 text-sm mt-1">Please contact your counsellor with your child's Reference Code.</p>
        </div>
      )}

      {data?.linked && (
        <>
          <div className="mt-6 bg-white rounded-2xl border border-slate-100 p-5 flex items-center gap-4" data-testid="parent-student-card">
            <div className="w-12 h-12 rounded-xl bg-[#004030] text-[#e0c05a] grid place-items-center"><GraduationCap size={24} /></div>
            <div className="flex-1">
              <p className="font-semibold text-[#012a1e]">{data.student.name}</p>
              <p className="text-sm text-slate-500">{data.student.student_code} · {data.student.country || "—"}</p>
            </div>
            {data.profile?.review_status && (
              <span className={`text-xs font-semibold px-3 py-1 rounded-full ${REVIEW_COLORS[data.profile.review_status] || "bg-slate-100"}`}>{data.profile.review_status}</span>
            )}
          </div>

          <h2 className="font-display text-xl font-bold text-[#012a1e] mt-8">Applications</h2>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            {data.applications.map((a) => (
              <div key={a.id} className="bg-white rounded-2xl border border-slate-100 p-5" data-testid={`parent-app-${a.id}`}>
                <div className="flex items-start justify-between">
                  <FileText size={20} className="text-[#004030]" />
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full ${STATUS_COLORS[a.status] || "bg-slate-100"}`}>{a.status}</span>
                </div>
                <p className="font-semibold text-[#012a1e] mt-3">{a.university_name}</p>
                <p className="text-sm text-slate-500">{a.programme || "—"} · {a.intake || "—"}</p>
                <div className="mt-3 flex items-center gap-2 text-xs">
                  <CreditCard size={14} className="text-slate-400" />
                  {a.fee_status === "Paid"
                    ? <span className="text-green-700 font-semibold flex items-center gap-1"><CheckCircle2 size={13} /> Fee paid{a.fee_amount ? ` (${(a.fee_currency || "").toUpperCase()} ${a.fee_amount})` : ""}</span>
                    : <span className="text-slate-500">Application fee: {a.fee_status || "Unpaid"}</span>}
                </div>
              </div>
            ))}
            {!data.applications.length && (
              <div className="sm:col-span-2 bg-white rounded-2xl border border-dashed border-slate-200 p-10 text-center text-slate-400">No applications yet.</div>
            )}
          </div>
        </>
      )}
    </PortalShell>
  );
}
